import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import IForm from '../../../types/IForm';
import { ActionState, ErrorState } from './useContentWrapper';
import { getPosition, Position } from './ContentWrapper.utils';
import { ContentWrapperActionType, ContentWrapperBlocks } from './ContentWrapper.types';

export type SharedContextAndProviderProps = {
  onDeleteSuccess(uuid: string): void;
  onMovingFailure(uuid: string): void;
  onMovingSuccess(actionKey: string, uuid: string, elementType: string): void;
  refetch(uuid: string): Promise<void>;
  nextBlockIsRichtext: boolean;
  belowIsCollapsibleContainer: number | undefined;
  aboveIsCollapsibleContainer: number | undefined;
  index: number;
  parentIndex?: number;
  grandParentIndex?: number;
  error?: ErrorState | null;
  action?: ActionState | null;
  numBlocks: number;
  setAction: React.Dispatch<React.SetStateAction<ActionState | null>>;
  setError: React.Dispatch<React.SetStateAction<ErrorState | null>>;
  moveOutsideOfCollapsibleBlock?: (parentUuid: string | undefined, block: ContentWrapperBlocks, actionKey: string)
  => void,
  registerCollapsibleStateHandlerCallback?: (uuid: string, callback: (block: ContentWrapperBlocks, actionKey: string)
  => void) => void,
  onConnectSuccess?: (uuid: string, resp: any) => void,
  onCollapsibleBlockContentWrapperChange?: (
    collapsibleBlockUuid: string,
    collapsibleContentNumberOfBlocks: number,
  ) => void,
};

export type ContentWrapperContext = SharedContextAndProviderProps & {
  actions: Record<ContentWrapperActionType, IForm>;
  position: Position | null;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
};

export type ContentWrapperContextProvider = SharedContextAndProviderProps & {
  block: ContentWrapperBlocks;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
};

export const ContentWrapperContext = createContext<ContentWrapperContext | null>(null);
export const useContentWrapperContext = () => useContext(ContentWrapperContext);

export function ContentWrapperContextProvider({
  children,
  block,
  index,
  parentIndex,
  grandParentIndex,
  error,
  action,
  setAction,
  addBlock,
  setError,
  numBlocks,
  nextBlockIsRichtext,
  onDeleteSuccess,
  onMovingFailure,
  onMovingSuccess,
  refetch,
  moveOutsideOfCollapsibleBlock,
  registerCollapsibleStateHandlerCallback,
  belowIsCollapsibleContainer,
  aboveIsCollapsibleContainer,
  onConnectSuccess,
  onCollapsibleBlockContentWrapperChange,
}: PropsWithChildren<ContentWrapperContextProvider>) {
  const contentWrapperProps = useMemo(
    () => ({
      actions: block.actions,
      parentIndex: parentIndex,
      grandParentIndex: grandParentIndex,
      error,
      index,
      position: getPosition(index, parentIndex, block.elementType, false, undefined),
      action,
      setAction,
      addBlock,
      setError,
      nextBlockIsRichtext,
      numBlocks,
      onDeleteSuccess,
      onMovingFailure,
      onMovingSuccess,
      refetch,
      moveOutsideOfCollapsibleBlock,
      registerCollapsibleStateHandlerCallback,
      belowIsCollapsibleContainer,
      aboveIsCollapsibleContainer,
      onConnectSuccess,
      onCollapsibleBlockContentWrapperChange,
    }),
    [
      block.actions,
      block.elementType,
      parentIndex,
      grandParentIndex,
      error,
      index,
      action,
      setAction,
      addBlock,
      setError,
      nextBlockIsRichtext,
      numBlocks,
      onDeleteSuccess,
      onMovingFailure,
      onMovingSuccess,
      refetch,
      moveOutsideOfCollapsibleBlock,
      registerCollapsibleStateHandlerCallback,
      belowIsCollapsibleContainer,
      aboveIsCollapsibleContainer,
      onConnectSuccess,
      onCollapsibleBlockContentWrapperChange,
    ],
  );

  return <ContentWrapperContext.Provider value={contentWrapperProps}>{children}</ContentWrapperContext.Provider>;
}
